import React from 'react';
import { Box, Container, Typography, Chip } from '@mui/material';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useT } from '../i18n/useT';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import HttpsIcon from '@mui/icons-material/Https';
import HistoryToggleOffIcon from '@mui/icons-material/HistoryToggleOff';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';

const CHIP_ICONS = [
  <RocketLaunchIcon sx={{ fontSize: '1.05rem' }} />,
  <HttpsIcon sx={{ fontSize: '1.05rem' }} />,
  <HistoryToggleOffIcon sx={{ fontSize: '1.05rem' }} />,
];

const Deployment: React.FC = () => {
  const { t, r } = useT();
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  const chips = r.deployment.chips;
  const browser = r.deployment.browser;

  return (
    <Box component="section" id="deployment" sx={{ py: { xs: 8, md: 12 }, backgroundColor: 'white' }}>
      <Container maxWidth="lg">
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: { xs: 4, md: 8 },
            alignItems: 'center',
          }}
        >
          <motion.div
            ref={ref}
            initial={{ opacity: 0, x: -30 }}
            animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: -30 }}
            transition={{ duration: 0.8 }}
          >
            <Typography
              component="span"
              sx={{
                color: '#64748B',
                fontWeight: 700,
                fontSize: '0.8rem',
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                mb: 2,
                display: 'block',
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              }}
            >
              {t('deployment.eyebrow')}
            </Typography>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 800,
                fontSize: { xs: '1.8rem', md: '2.4rem' },
                mb: 2,
                color: '#0F172A',
                lineHeight: 1.15,
                letterSpacing: '-0.02em',
              }}
            >
              {t('deployment.title')}
            </Typography>
            <Typography variant="body1" sx={{ color: '#475569', lineHeight: 1.7, fontSize: '1.05rem', mb: 3 }}>
              {t('deployment.subtitle')}
            </Typography>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.2, mb: 4 }}>
              {chips.map((chip, idx) => (
                <Chip
                  key={chip}
                  icon={CHIP_ICONS[idx]}
                  label={chip}
                  sx={{
                    px: 1,
                    py: 2.5,
                    fontWeight: 600,
                    backgroundColor: 'white',
                    border: '1px solid #E2E8F0',
                    color: '#0F172A',
                    '& .MuiChip-icon': { color: '#0F172A' },
                  }}
                />
              ))}
            </Box>

            <Box
              sx={{
                p: 3,
                borderRadius: 2,
                backgroundColor: '#F8FAFC',
                border: '1px solid #E2E8F0',
                borderLeft: '3px solid #0F172A',
              }}
            >
              <Typography
                sx={{
                  color: '#0F172A',
                  fontWeight: 700,
                  fontSize: { xs: '1rem', md: '1.1rem' },
                  lineHeight: 1.4,
                }}
              >
                {t('deployment.sameProductTagline')}
              </Typography>
            </Box>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: 30 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            {/* Browser-window mockup of the deployed app */}
            <Box
              role="img"
              aria-label={`${browser.url} · ${browser.statusValue} · ${browser.versionValue}`}
              sx={{
                borderRadius: 2.5,
                overflow: 'hidden',
                border: '1px solid #E2E8F0',
                boxShadow: '0 24px 60px rgba(15, 23, 42, 0.12)',
                backgroundColor: 'white',
              }}
            >
              {/* Browser chrome */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  px: 2,
                  py: 1.5,
                  backgroundColor: '#F1F5F9',
                  borderBottom: '1px solid #E2E8F0',
                }}
              >
                <Box sx={{ display: 'flex', gap: 0.7 }} aria-hidden>
                  {['#FF5F57', '#FEBC2E', '#28C840'].map((c) => (
                    <Box key={c} sx={{ width: 11, height: 11, borderRadius: '50%', backgroundColor: c }} />
                  ))}
                </Box>
                <Box
                  sx={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 1.5,
                    py: 0.6,
                    borderRadius: 1.2,
                    backgroundColor: 'white',
                    border: '1px solid #E2E8F0',
                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                    fontSize: '0.82rem',
                    color: '#475569',
                  }}
                >
                  <LockRoundedIcon sx={{ fontSize: '0.95rem', color: '#22C55E' }} aria-hidden />
                  <Typography
                    component="span"
                    sx={{
                      fontFamily: 'inherit',
                      fontSize: 'inherit',
                      color: 'inherit',
                      lineHeight: 1,
                    }}
                  >
                    <Box component="span" sx={{ color: '#94A3B8' }}>https://</Box>
                    {browser.url}
                  </Typography>
                </Box>
              </Box>

              {/* Body */}
              <Box
                sx={{
                  position: 'relative',
                  px: { xs: 3, md: 4 },
                  py: { xs: 4, md: 5 },
                  backgroundColor: 'rgb(13, 13, 37)',
                  color: 'white',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    inset: 0,
                    background:
                      'radial-gradient(circle at 80% 20%, rgba(0, 245, 255, 0.10) 0%, transparent 55%), radial-gradient(circle at 15% 90%, rgba(255, 0, 255, 0.08) 0%, transparent 55%)',
                    pointerEvents: 'none',
                  },
                }}
              >
                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', gap: 2.2 }}>
                  {/* Live status */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        backgroundColor: '#22C55E',
                        boxShadow: '0 0 0 4px rgba(34, 197, 94, 0.22)',
                      }}
                      aria-hidden
                    />
                    <Typography
                      component="span"
                      sx={{
                        color: 'rgba(255, 255, 255, 0.55)',
                        fontWeight: 700,
                        fontSize: '0.72rem',
                        letterSpacing: '0.18em',
                        textTransform: 'uppercase',
                        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                      }}
                    >
                      {browser.statusLabel}
                    </Typography>
                    <Typography
                      component="span"
                      sx={{
                        color: 'white',
                        fontWeight: 700,
                        fontSize: '0.95rem',
                      }}
                    >
                      {browser.statusValue}
                    </Typography>
                  </Box>

                  {/* App + version */}
                  <Box>
                    <Typography
                      sx={{
                        color: 'white',
                        fontWeight: 700,
                        fontSize: { xs: '1.3rem', md: '1.6rem' },
                        letterSpacing: '-0.01em',
                        lineHeight: 1.2,
                        mb: 0.8,
                      }}
                    >
                      {browser.url}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'rgba(255, 255, 255, 0.6)' }}>
                      <Typography
                        component="span"
                        sx={{
                          fontSize: '0.78rem',
                          letterSpacing: '0.12em',
                          textTransform: 'uppercase',
                          fontWeight: 700,
                          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                          color: 'rgba(255, 255, 255, 0.45)',
                        }}
                      >
                        {browser.versionLabel}
                      </Typography>
                      <Typography component="span" sx={{ fontSize: '0.88rem', color: 'rgba(255, 255, 255, 0.75)' }}>
                        {browser.versionValue}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Footer chips */}
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 1,
                      pt: 1.5,
                      borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                    }}
                  >
                    {browser.footerChips.map((label) => (
                      <Box
                        key={label}
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.6,
                          px: 1.2,
                          py: 0.4,
                          borderRadius: 999,
                          backgroundColor: 'rgba(255, 255, 255, 0.08)',
                          border: '1px solid rgba(255, 255, 255, 0.16)',
                          fontWeight: 600,
                          fontSize: '0.78rem',
                          color: 'rgba(255, 255, 255, 0.92)',
                        }}
                      >
                        <CheckRoundedIcon sx={{ fontSize: '0.95rem', color: '#22C55E' }} aria-hidden />
                        {label}
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>
            </Box>
          </motion.div>
        </Box>
      </Container>
    </Box>
  );
};

export default Deployment;
